/**
 * Keyboard Shortcuts Help Modal
 * Shows all available keyboard shortcuts
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Navigation, MousePointer, Headphones, HelpCircle } from 'lucide-react';
import { useLawityContext } from '../../context/LawityContext';
import { backdropVariants, modalVariants } from '../../design-system/animations';
import { formatShortcut } from '../../hooks/useKeyboardNavigation';

interface ShortcutGroup {
  title: string;
  icon: React.ReactNode;
  shortcuts: Array<{ key: string; description: string }>;
}

const shortcutGroups: ShortcutGroup[] = [
  {
    title: 'Navigation',
    icon: <Navigation className="w-4 h-4" />,
    shortcuts: [
      { key: 'g h', description: 'Go to Home/Dashboard' },
      { key: 'g m', description: 'Go to Matters' },
      { key: 'g c', description: 'Go to Clients' },
      { key: 'g d', description: 'Go to Documents' },
      { key: 'g b', description: 'Go to Billing' },
      { key: 'g s', description: 'Go to Settings' },
      { key: 'g p', description: 'Go to Profile' },
    ],
  },
  {
    title: 'Actions',
    icon: <MousePointer className="w-4 h-4" />,
    shortcuts: [
      { key: 'mod+k', description: 'Open command palette' },
      { key: '/', description: 'Global search' },
      { key: 'n', description: 'New item (context-aware)' },
      { key: 'e', description: 'Edit current item' },
      { key: 'Escape', description: 'Close/Cancel/Back' },
    ],
  },
  {
    title: 'Decision Stack',
    icon: <MousePointer className="w-4 h-4" />,
    shortcuts: [
      { key: 'ArrowRight', description: 'Approve/Accept' },
      { key: 'ArrowLeft', description: 'Reject/Snooze' },
      { key: 'Space', description: 'Expand details (God Mode)' },
      { key: 'Enter', description: 'Confirm action' },
      { key: 'mod+z', description: 'Undo last action' },
    ],
  },
  {
    title: 'Audio Player',
    icon: <Headphones className="w-4 h-4" />,
    shortcuts: [
      { key: 'p', description: 'Play/Pause briefing' },
      { key: 's', description: 'Skip to next segment' },
      { key: 'v', description: 'Toggle voice mode' },
      { key: 'z', description: 'Toggle zen mode' },
    ],
  },
  {
    title: 'Help',
    icon: <HelpCircle className="w-4 h-4" />,
    shortcuts: [
      { key: '?', description: 'Show this help' },
    ],
  },
];

export function KeyboardShortcutsHelp() {
  const { isHelpOpen, setHelpOpen } = useLawityContext();

  return (
    <AnimatePresence>
      {isHelpOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={() => setHelpOpen(false)}
          />

          {/* Modal */}
          <motion.div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[80vh] bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden z-50"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Keyboard Shortcuts
              </h2>
              <button
                onClick={() => setHelpOpen(false)}
                className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <div className="grid md:grid-cols-2 gap-6">
                {shortcutGroups.map((group) => (
                  <div key={group.title}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-neutral-400">{group.icon}</span>
                      <h3 className="font-medium text-neutral-900 dark:text-white">
                        {group.title}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {group.shortcuts.map((shortcut) => (
                        <div
                          key={shortcut.key}
                          className="flex items-center justify-between py-1.5"
                        >
                          <span className="text-sm text-neutral-600 dark:text-neutral-400">
                            {shortcut.description}
                          </span>
                          <kbd className="px-2 py-1 text-xs font-mono bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 rounded border border-neutral-200 dark:border-neutral-700">
                            {formatShortcut(shortcut.key)}
                          </kbd>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 bg-focus-50 dark:bg-focus-900/20 rounded-xl">
                <h4 className="font-medium text-focus-700 dark:text-focus-300 mb-2">
                  Pro Tips
                </h4>
                <ul className="text-sm text-focus-600 dark:text-focus-400 space-y-1">
                  <li>• Press <kbd className="px-1 bg-focus-100 dark:bg-focus-800 rounded">g</kbd> then a letter to navigate quickly</li>
                  <li>• Use <kbd className="px-1 bg-focus-100 dark:bg-focus-800 rounded">⌘K</kbd> to access any command instantly</li>
                  <li>• Swipe or use arrow keys in the Decision Stack</li>
                  <li>• Press <kbd className="px-1 bg-focus-100 dark:bg-focus-800 rounded">Space</kbd> to see God Mode details</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
              <p className="text-xs text-neutral-500 text-center">
                Press <kbd className="px-1 bg-neutral-200 dark:bg-neutral-700 rounded">?</kbd> anywhere to show this help
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default KeyboardShortcutsHelp;
